import logger from '@finn-no/fiaas-logger';
import type { NextPage } from 'next';
import { useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { getPageCounter } from 'shared/prometheus/prometheus';
import type { SspContext } from 'shared/types';
import { fetchHotel, fetchOffersForHotel } from '@/api/fetcher';
import { HotelPageLayout } from '@/components/hotel/HotelPageLayout';
import { config, feConfig } from '@/config';
import { useHotelSeo } from '@/hooks/useHotelSeo';
import type { Filters, HotelOffersResponse, PaleState } from '@/interfaces';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions } from '@/state/filterActions';
import { actions as hotelActions } from '@/state/hotelActions';
import { actions as hotelResultActions } from '@/state/hotelResultActions';
import { wrapper } from '@/state/store';

const getPreferredSupplier = (hotelResults: HotelOffersResponse): string | undefined =>
    hotelResults.offers.length > 0 ? hotelResults.offers[0].supplier : undefined;

const HotelPage: NextPage = (props) => {
    wrapper.useHydration(props);
    const hotel = useSelector((state: PaleState) => state.hotel);

    const { title, description, url, image } = useHotelSeo(hotel);

    return (
        <>
            <SimpleHead title={title} description={description} canonical={url} imageUrl={image} />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <HotelPageLayout />
            </Main>
        </>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }: SspContext) => {
    await runAllMiddlewares(req, res, query);

    const filters: Partial<Filters> = browserQueryParamsToFilters(query);
    store.dispatch(actions.filter(filters));

    const apiQueryParams = filtersToApiQueryParams(filters);

    try {
        const offerData = await fetchOffersForHotel(config.get('pp.apiUrlInternal'), apiQueryParams);
        if (offerData) {
            store.dispatch(hotelResultActions.setResults(offerData));

            try {
                const preferredSupplier = getPreferredSupplier(offerData);
                const hotelData = await fetchHotel(config.get('pp.apiUrlInternal'), { ...apiQueryParams, preferredSupplier });
                store.dispatch(hotelActions.setHotel(hotelData));
            } catch (error) {
                // Hotel fetch failed, but offers succeeded
                logger.error(`Failed to fetch hotel data for hotel page: ${error}`);
            }
        } else {
            store.dispatch(hotelResultActions.setNotModified());
        }
    } catch (error) {
        store.dispatch(hotelResultActions.setError());
        logger.error(`Failed to fetch hotel offers for hotel page: ${error}`);
    }

    getPageCounter().inc({ app: 'pale-pp-web', page: 'hotell' });

    return {
        props: {
            hasLegalBasis: res.locals?.hasLegalBasis ?? false,
            unleash: res.locals?.unleash ?? null,
            config: feConfig,
        },
    };
});

export default HotelPage;
